import { useEffect, useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Cpu } from "lucide-react";

type Provider = "lovable" | "openai_compatible";

interface Config {
  provider: Provider;
  base_url: string;
  api_key: string;
  model_id: string;
  enabled: boolean;
}

const DEFAULT: Config = {
  provider: "lovable",
  base_url: "",
  api_key: "",
  model_id: "",
  enabled: false,
};

export default function AdminAIModel() {
  const [config, setConfig] = useState<Config>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("ai_model_config" as any)
        .select("*")
        .eq("id", true)
        .maybeSingle();
      if (error) toast.error(error.message);
      if (data) {
        const d = data as any;
        setConfig({
          provider: d.provider ?? "lovable",
          base_url: d.base_url ?? "",
          api_key: d.api_key ?? "",
          model_id: d.model_id ?? "",
          enabled: !!d.enabled,
        });
      }
      setLoading(false);
    })();
  }, []);

  async function save() {
    setSaving(true);
    const { data: userRes } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("ai_model_config" as any)
      .upsert({
        id: true,
        provider: config.provider,
        base_url: config.base_url || null,
        api_key: config.api_key || null,
        model_id: config.model_id || null,
        enabled: config.enabled,
        updated_at: new Date().toISOString(),
        updated_by: userRes.user?.id ?? null,
      } as any);
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success("AI model configuration saved");
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container max-w-3xl py-8">
        <div className="flex items-center gap-2 mb-6">
          <Cpu className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">AI Model Configuration</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Chat model provider</CardTitle>
            <CardDescription>
              Route the AI chat through Lovable AI Gateway (default) or any OpenAI‑compatible
              endpoint like Ollama, LM Studio, vLLM, or a self‑hosted OpenAI proxy. Applies to
              every user's chat.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <div className="font-medium">Use custom provider</div>
                    <div className="text-xs text-muted-foreground">
                      When off, chat uses Lovable AI Gateway.
                    </div>
                  </div>
                  <Switch
                    checked={config.enabled && config.provider === "openai_compatible"}
                    onCheckedChange={(v) =>
                      setConfig((c) => ({
                        ...c,
                        enabled: v,
                        provider: v ? "openai_compatible" : "lovable",
                      }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select
                    value={config.provider}
                    onValueChange={(v) => setConfig((c) => ({ ...c, provider: v as Provider }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lovable">Lovable AI Gateway (default)</SelectItem>
                      <SelectItem value="openai_compatible">OpenAI‑compatible endpoint</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {config.provider === "openai_compatible" && (
                  <>
                    <div className="space-y-2">
                      <Label>Base URL</Label>
                      <Input
                        placeholder="http://localhost:11434/v1  (Ollama)"
                        value={config.base_url}
                        onChange={(e) => setConfig((c) => ({ ...c, base_url: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">
                        Examples: <code>http://localhost:11434/v1</code> (Ollama),{" "}
                        <code>http://localhost:1234/v1</code> (LM Studio),{" "}
                        <code>https://api.groq.com/openai/v1</code>.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Model ID</Label>
                      <Input
                        placeholder="llama3.1:8b, qwen2.5-coder, gpt-oss, ..."
                        value={config.model_id}
                        onChange={(e) => setConfig((c) => ({ ...c, model_id: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>API key (optional)</Label>
                      <Input
                        type="password"
                        placeholder="Leave blank for local providers (Ollama / LM Studio)"
                        value={config.api_key}
                        onChange={(e) => setConfig((c) => ({ ...c, api_key: e.target.value }))}
                      />
                    </div>
                    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200 p-3 text-xs">
                      Local endpoints (localhost) only work if the Supabase edge function can
                      reach them from the internet. For local Ollama/LM Studio, expose it via a
                      tunnel (ngrok, Cloudflare Tunnel) and paste the public URL here.
                    </div>
                  </>
                )}

                <Button onClick={save} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Save configuration
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
